import { client } from "./axiosClient";
import { store } from "../store";
import { baseUrl } from "utils/backendConfig";

function parseSSELines(text) {
  const events = [];
  let currentEvent = null;
  let currentData = [];

  const lines = text.split("\n");
  for (const line of lines) {
    if (line.startsWith("event: ")) {
      if (currentEvent && currentData.length > 0) {
        events.push({
          event: currentEvent,
          data: currentData.join("\n"),
        });
      }
      currentEvent = line.slice(7);
      currentData = [];
    } else if (line.startsWith("data: ")) {
      currentData.push(line.slice(6));
    } else if (line === "" && currentEvent) {
      events.push({
        event: currentEvent,
        data: currentData.join("\n"),
      });
      currentEvent = null;
      currentData = [];
    }
  }
  if (currentEvent && currentData.length > 0) {
    events.push({
      event: currentEvent,
      data: currentData.join("\n"),
    });
  }
  return events;
}

async function ensureFreshToken() {
  try {
    await client.post("api/awake/");
  } catch {
    // ignore — the interceptor will handle refresh
  }
  const tokens = store.getState().auth.authTokens;
  return tokens ? tokens.access : null;
}

export async function sendChatMessage(message, history, callbacks, signal) {
  const {
    filterContext,
    onThinkingStep,
    onContentDelta,
    onContentReset,
    onContent,
    onArtifacts,
    onSources,
    onDone,
    onError,
  } = callbacks;

  const token = await ensureFreshToken();
  if (!token) {
    if (onError) onError(new Error("Not authenticated"));
    return;
  }

  try {
    const body = { message, history };
    if (filterContext) {
      body.page_context = filterContext;
    }

    const fetchOpts = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify(body),
    };
    if (signal) fetchOpts.signal = signal;

    const response = await fetch(baseUrl + "api/chat/message/", fetchOpts);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (onError) {
        onError(
          new Error(
            errorData.error_message || errorData.error || "Request failed",
          ),
        );
      }
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    let streamDone = false;
    let gotDoneEvent = false;
    while (!streamDone) {
      const { done, value } = await reader.read();
      if (done) {
        streamDone = true;
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      const lastComplete = buffer.lastIndexOf("\n\n");
      if (lastComplete === -1) continue;
      const complete = buffer.substring(0, lastComplete + 2);
      buffer = buffer.substring(lastComplete + 2);

      const events = parseSSELines(complete);

      for (const evt of events) {
        try {
          const data = JSON.parse(evt.data);
          switch (evt.event) {
            case "thinking":
              if (onThinkingStep) onThinkingStep(data.step);
              break;
            case "content_delta":
              if (onContentDelta) onContentDelta(data.delta);
              break;
            case "content_reset":
              if (onContentReset) onContentReset();
              break;
            case "content":
              if (onContent) onContent(data.content);
              break;
            case "artifacts":
              if (onArtifacts) onArtifacts(data);
              break;
            case "sources":
              if (onSources) onSources(data);
              break;
            case "error":
              if (onError) onError(new Error(data.message || "Server error"));
              gotDoneEvent = true;
              break;
            case "done":
              if (onDone) onDone(data);
              gotDoneEvent = true;
              break;
            default:
              break;
          }
        } catch (parseErr) {
          // Partial data in buffer, ignore
        }
      }
    }
    if (!gotDoneEvent) {
      if (onDone) onDone();
    }
  } catch (err) {
    if (err.name === "AbortError") return;
    if (onError) onError(err);
  }
}

export async function submitChatRating(
  rating,
  messageIndex,
  userMessagePreview,
  aiResponsePreview,
  conversationSnapshot,
  usageLogId,
  feedbackCategories,
) {
  const payload = {
    rating,
    message_index: messageIndex,
    user_message_preview: userMessagePreview,
    ai_response_preview: aiResponsePreview,
    conversation_snapshot: conversationSnapshot,
  };
  if (usageLogId) payload.usage_log_id = usageLogId;
  if (feedbackCategories?.length)
    payload.feedback_categories = feedbackCategories;
  const response = await client.post("api/chat/rating/", payload);
  return response.data;
}

export async function submitChatFeedback(
  feedbackText,
  conversation,
  usageLogId,
) {
  const payload = {
    feedback: feedbackText,
    conversation,
  };
  if (usageLogId) payload.usage_log_id = usageLogId;
  const response = await client.post("api/chat/feedback/", payload);
  return response.data;
}

export async function evaluateResponse(usageLogId, userMessage, aiResponse) {
  const payload = {};
  if (usageLogId) payload.usage_log_id = usageLogId;
  if (userMessage) payload.user_message = userMessage;
  if (aiResponse) payload.ai_response = aiResponse;
  const response = await client.post("api/chat/evaluate/", payload);
  return response.data;
}

export async function submitHumanScore(usageLogId, scores, feedbackText) {
  const payload = {
    usage_log_id: usageLogId,
    ...scores,
  };
  if (feedbackText) payload.feedback_text = feedbackText;
  const response = await client.post("api/chat/human-score/", payload);
  return response.data;
}

export async function saveConversation(sessionId, messages, metadata) {
  const response = await client.post("api/chat/conversation/save/", {
    session_id: sessionId,
    messages,
    metadata,
  });
  return response.data;
}
